import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import api, { getUser, isAuthed } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RoiCalculator from "@/components/RoiCalculator";
import BoostCard from "@/pages/dashboard/BoostCard";
import {
  Check, X, ArrowRight, Sparkles, Building2, Coffee, ArrowRightLeft, Users,
  Briefcase, Star, ShieldCheck, Zap, Crown, MessageCircle, Gift, Tag, Timer
} from "lucide-react";

const T = {
  hero_kicker: { it: "Prezzi trasparenti", en: "Transparent pricing" },
  hero_title: {
    it: "Paghi solo quando incassi.\nAbbonati solo se vuoi crescere.",
    en: "Pay only when you earn.\nSubscribe only to grow.",
  },
  hero_sub: {
    it: "Nessun costo di iscrizione, nessuna sorpresa. Ogni fee è documentata qui sotto: dalle prenotazioni dirette alla colazione centralizzata, fino ai piani Pro ed Enterprise.",
    en: "No sign-up fee, no surprises. Every commission is documented below: from direct bookings to centralized breakfast, up to Pro and Enterprise plans.",
  },
  sec1: { it: "Commissioni transazionali", en: "Transactional fees" },
  sec1_sub: { it: "Attive per tutti gli account. Paghi solo per ciò che generi.", en: "Active for every account. You pay only for what you generate." },
  sec2: { it: "Piani abbonamento", en: "Subscription plans" },
  sec2_sub: { it: "Sblocca automazioni avanzate e riduci le commissioni.", en: "Unlock advanced automations and reduce commissions." },
  faq: { it: "Domande frequenti", en: "FAQ" },
};
const t = (k, lang) => T[k][lang] || T[k].it;

function TxRow({ icon: Icon, title, subtitle, fee, note }) {
  return (
    <div className="flex items-start gap-4 border-b border-border py-5 last:border-0">
      <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="w-5 h-5"/></div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl">{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>
        {note && <div className="text-[11px] text-muted-foreground mt-1 italic">{note}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-2xl text-primary">{fee}</div>
      </div>
    </div>
  );
}

function PlanCard({ tier, price, subtitle, highlights, cta, testid, featured, badge, currentFee }) {
  return (
    <div
      className={`relative rounded-lg border p-6 flex flex-col ${featured ? "border-primary shadow-xl scale-[1.02] bg-card" : "border-border bg-card"}`}
      data-testid={testid}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest rounded-full px-3 py-1 shadow">
          {badge}
        </div>
      )}
      <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold">{tier}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-display text-5xl tracking-tight">{price}</span>
        {price !== "€0" && <span className="text-sm text-muted-foreground">/mese</span>}
      </div>
      <p className="text-sm text-muted-foreground mt-2 mb-4">{subtitle}</p>
      <div className="mb-4 text-xs bg-sand rounded-md px-3 py-2 flex items-center gap-2">
        <span className="uppercase tracking-widest text-muted-foreground">Fee prenotazioni:</span>
        <span className="font-display text-primary text-base">{currentFee}</span>
      </div>
      <ul className="space-y-2 text-sm flex-1">
        {highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2">
            {h.included ? <Check className="w-4 h-4 text-sage mt-0.5 shrink-0"/> : <X className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0 opacity-40"/>}
            <span className={h.included ? "" : "text-muted-foreground line-through opacity-60"}>{h.text}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">{cta}</div>
    </div>
  );
}

export default function Pricing() {
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const { hash } = useLocation();
  const authed = isAuthed();

  // Scroll to the requested anchor once mounted (e.g. #boost-featured from the sidebar links)
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const t = setTimeout(() => {
      const el = document.getElementById(id) || document.getElementById("boost");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [hash]);

  const subscribe = async (tier) => {
    const user = getUser();
    if (!user) {
      toast.info(IT ? "Registrati o accedi per attivare l'abbonamento" : "Sign up or log in to subscribe");
      nav("/auth?mode=login");
      return;
    }
    try {
      const r = await api.post("/subscriptions/checkout", { tier, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10" data-testid="pricing-hero">
        <div className="uppercase tracking-[0.3em] text-[10px] text-sage font-semibold mb-3">
          {t("hero_kicker", lang)}
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight whitespace-pre-line">
          {t("hero_title", lang)}
        </h1>
        <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
          {t("hero_sub", lang)}
        </p>
      </section>

      {/* Special Offers — dynamic banners managed from Admin */}
      <SpecialOffersSection IT={IT}/>

      {/* Transactional fees */}
      <section className="max-w-5xl mx-auto px-6 py-12" data-testid="pricing-transactional">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">01</div>
            <h2 className="font-display text-4xl">{t("sec1", lang)}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("sec1_sub", lang)}</p>
          </div>
        </div>
        <div className="border border-border rounded-lg bg-card divide-y divide-border">
          <TxRow
            icon={Building2}
            title={IT ? "Prenotazioni dirette" : "Direct bookings"}
            subtitle={IT ? "Guest paga con Stripe direttamente sulla vetrina. Accrediti immediato al netto della fee." : "Guest pays via Stripe on your showcase. Instant payout minus fee."}
            fee="8%"
            note={IT ? "Ridotta al 6% con piano Pro, al 5% con Enterprise · esclusi costi Stripe gestione pagamenti (~1,5% + €0,25/transazione)" : "Reduced to 6% with Pro, 5% with Enterprise · Stripe payment processing fees excluded (~1.5% + €0.25/tx)"}
          />
          <TxRow
            icon={ArrowRightLeft}
            title={IT ? "Scambio camere tra strutture" : "Room swap between properties"}
            subtitle={IT ? "Quando una struttura riceve un overbook, un'altra della rete assorbe il guest." : "When a property is overbooked, another in the network absorbs the guest."}
            fee="10%"
            note={IT ? "Sul valore della prenotazione swap-ata" : "On the swapped booking value"}
          />
          <TxRow
            icon={Coffee}
            title={IT ? "Colazione centralizzata" : "Centralized breakfast"}
            subtitle={IT ? "Il tuo B&B non ha cucina? Il guest fa colazione da una struttura partner convenzionata." : "No kitchen at your B&B? Guests have breakfast at a partnered property."}
            fee="€ 12,00"
            note={IT ? "Per coperto, indipendentemente dal menu offerto" : "Per meal, regardless of menu"}
          />
          <TxRow
            icon={Users}
            title={IT ? "Richieste di gruppo (split)" : "Group requests (split)"}
            subtitle={IT ? "Un tour operator prenota N camere → distribuite automaticamente su più strutture." : "A tour operator books N rooms → auto-distributed across multiple properties."}
            fee="13%"
            note={IT ? "Sul totale del gruppo, ripartito pro-quota" : "On the group total, pro-rated"}
          />
          <TxRow
            icon={Briefcase}
            title={IT ? "Trade B2B — bundle multi-struttura" : "Trade B2B — multi-property bundle"}
            subtitle={IT ? "Agenzie e tour operator costruiscono pacchetti tour su più strutture in un unico checkout." : "Agencies build multi-property tour packages in a single checkout."}
            fee="6%"
            note={IT ? "Sul totale del bundle, addebitata a carico dell'agenzia" : "On bundle total, charged to the agency"}
          />
        </div>
      </section>

      {/* Subscription plans */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-plans">
        <div className="mb-8">
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">02</div>
          <h2 className="font-display text-4xl">{t("sec2", lang)}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("sec2_sub", lang)}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <PlanCard
            testid="plan-free"
            tier={IT ? "Base" : "Free"}
            price="€0"
            subtitle={IT ? "Perfetto per iniziare. Vetrina pubblica, prenotazioni dirette, mappa." : "Perfect to start. Public showcase, direct bookings, map."}
            currentFee="8%"
            highlights={[
              { included: true, text: IT ? "Vetrina bilingue IT/EN" : "Bilingual IT/EN showcase" },
              { included: true, text: IT ? "Prenotazioni dirette Stripe" : "Direct Stripe bookings" },
              { included: true, text: IT ? "Mappa e ricerca geografica" : "Map + geo search" },
              { included: true, text: IT ? "Scambio camere e colazione network" : "Room swap + breakfast network" },
              { included: true, text: IT ? "Sync iCal (Booking, Airbnb, Expedia)" : "iCal sync" },
              { included: false, text: IT ? "Analytics avanzati (ADR, RevPAR, mix)" : "Advanced analytics" },
              { included: false, text: IT ? "Channel Manager OTA integrato" : "Integrated OTA Channel Manager" },
              { included: false, text: IT ? "AI Chatbot 24/7 sulla vetrina" : "AI Chatbot 24/7" },
              { included: false, text: IT ? "Notifiche push web" : "Web push notifications" },
              { included: false, text: IT ? "White label + API custom" : "White label + custom API" },
            ]}
            cta={
              <Link to="/auth?mode=register" data-testid="plan-free-cta">
                <Button variant="outline" className="w-full lift">
                  {IT ? "Inizia gratis" : "Get started free"}
                </Button>
              </Link>
            }
          />

          <PlanCard
            testid="plan-pro"
            tier="Pro"
            price="€49"
            subtitle={IT ? "Per host che vogliono crescere e automatizzare tutto." : "For hosts who want to grow and automate everything."}
            currentFee="6%"
            featured
            badge={IT ? "Consigliato" : "Popular"}
            highlights={[
              { included: true, text: IT ? "Tutto del piano Base" : "Everything in Free" },
              { included: true, text: IT ? "Analytics avanzati (ADR, RevPAR, mix)" : "Advanced analytics" },
              { included: true, text: IT ? "Channel Manager OTA (5 provider)" : "OTA Channel Manager (5 providers)" },
              { included: true, text: IT ? "AI Chatbot Gemini sulla vetrina" : "Gemini AI Chatbot" },
              { included: true, text: IT ? "AI Copy generator (descrizioni)" : "AI Copy generator" },
              { included: true, text: IT ? "Notifiche push web + email" : "Web push + email notifications" },
              { included: true, text: IT ? "Export CSV analytics" : "CSV analytics export" },
              { included: true, text: IT ? "Commissioni prenotazioni ridotte al 6%" : "Booking commissions reduced to 6%" },
              { included: false, text: IT ? "🔒 Hospitality Assistant IA (solo Enterprise)" : "🔒 Hospitality Assistant AI (Enterprise only)" },
              { included: false, text: IT ? "White label + dominio personalizzato" : "White label + custom domain" },
              { included: false, text: IT ? "API custom + account manager" : "Custom API + account manager" },
            ]}
            cta={
              <Button
                onClick={() => subscribe("pro")}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
                data-testid="plan-pro-cta"
              >
                {IT ? "Prova Pro 30 giorni gratis" : "Try Pro 30 days free"}
              </Button>
            }
          />

          <PlanCard
            testid="plan-enterprise"
            tier="Enterprise"
            price="€199"
            subtitle={IT ? "Catene, network, franchising. Whitelabel + supporto dedicato." : "Chains, networks, franchising. Whitelabel + dedicated support."}
            currentFee="5%"
            highlights={[
              { included: true, text: IT ? "Tutto del piano Pro" : "Everything in Pro" },
              { included: true, text: IT ? "✨ Hospitality Assistant IA (Claude Sonnet)" : "✨ Hospitality Assistant AI (Claude Sonnet)" },
              { included: true, text: IT ? "White label completo" : "Full white label" },
              { included: true, text: IT ? "Dominio personalizzato" : "Custom domain" },
              { included: true, text: IT ? "API REST custom" : "Custom REST API" },
              { included: true, text: IT ? "Account manager dedicato" : "Dedicated account manager" },
              { included: true, text: IT ? "Onboarding assistito + formazione" : "Guided onboarding + training" },
              { included: true, text: IT ? "SLA 24h + supporto prioritario" : "24h SLA + priority support" },
              { included: true, text: IT ? "Report personalizzati (BI export)" : "Custom BI reports" },
              { included: true, text: IT ? "Commissioni prenotazioni ridotte al 5%" : "Booking commissions reduced to 5%" },
              { included: true, text: IT ? "Multi-struttura fino a 60 camere" : "Multi-property up to 60 rooms" },
            ]}
            cta={
              <Button
                onClick={() => subscribe("enterprise")}
                variant="outline"
                className="w-full lift"
                data-testid="plan-enterprise-cta"
              >
                {IT ? "Passa a Enterprise" : "Upgrade to Enterprise"} <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            }
          />
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {IT
            ? "Tutti i prezzi sono in EUR e escludono IVA. Fatturazione mensile o annuale (con 20% di sconto in caso di pagamento anticipato)."
            : "All prices in EUR, VAT excluded. Monthly or yearly billing (20% discount on prepaid annual)."}
        </p>
      </section>

      {/* Corporate — for hotels above 60 rooms */}
      <CorporateSection IT={IT}/>

      {/* Trust bar */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-3 gap-6 border-y border-border py-8">
          <div className="text-center">
            <ShieldCheck className="w-6 h-6 text-sage mx-auto mb-2"/>
            <div className="font-display text-xl">{IT ? "Nessun vincolo" : "No lock-in"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT ? "Cancella l'abbonamento quando vuoi, nessuna penale." : "Cancel anytime, no penalty."}
            </div>
          </div>
          <div className="text-center">
            <Sparkles className="w-6 h-6 text-primary mx-auto mb-2"/>
            <div className="font-display text-xl">{IT ? "Setup zero" : "Zero setup"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT ? "Iscriviti, pubblica, ricevi. Nessuna installazione." : "Sign up, publish, receive. No installs."}
            </div>
          </div>
          <div className="text-center">
            <Zap className="w-6 h-6 text-primary mx-auto mb-2"/>
            <div className="font-display text-xl">{IT ? "Prova Pro gratis 30 giorni" : "30 days Pro free trial"}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {IT ? "Prima fattura solo dopo il periodo di prova." : "First invoice only after trial."}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <RoiCalculator IT={IT} />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16" data-testid="pricing-faq">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1">03</div>
        <h2 className="font-display text-4xl mb-6">{t("faq", lang)}</h2>
        <div className="space-y-6">
          {[
            {
              q: IT ? "Le commissioni sono cumulative?" : "Are commissions cumulative?",
              a: IT
                ? "No. Ogni transazione ha una sola commissione: se un guest prenota direttamente in vetrina, paghi solo l'8% (o 6%/5% se sei Pro/Enterprise). Se invece la stessa camera viene 'swap-ata' a un partner, si applica il 10% dello swap. Le commissioni HospitalityHub non includono i costi Stripe di gestione pagamenti (~1,5% + €0,25 per transazione EU/UK)."
                : "No. Each transaction has a single fee: a direct booking is 8% (or 6%/5% with Pro/Enterprise). A swapped booking gets 10% instead. HospitalityHub fees exclude Stripe payment processing fees (~1.5% + €0.25 per EU/UK transaction).",
            },
            {
              q: IT ? "Come funziona la colazione a €12?" : "How does the €12 breakfast work?",
              a: IT
                ? "Se il tuo B&B non ha una cucina o una sala colazioni, il tuo guest può fare colazione presso una struttura partner della rete. Tu paghi €12 a coperto alla struttura ospitante, HospitalityHub non trattiene nulla oltre alla gestione del pagamento."
                : "If your B&B has no kitchen, guests can have breakfast at a partnered property. You pay €12 per meal to the host property. HospitalityHub doesn't charge extra beyond payment handling.",
            },
            {
              q: IT ? "Posso cambiare piano in qualsiasi momento?" : "Can I change plan anytime?",
              a: IT
                ? "Sì. Puoi passare da Base a Pro (o Pro→Enterprise) in qualsiasi momento dalla Dashboard. Il downgrade è possibile alla scadenza del ciclo di fatturazione."
                : "Yes. Upgrade from Free to Pro (or Pro→Enterprise) anytime from Dashboard. Downgrade takes effect at the end of the billing cycle.",
            },
            {
              q: IT ? "Serve la partita IVA per iscrivermi?" : "Do I need a VAT number to sign up?",
              a: IT
                ? "Serve solo per l'operatività commerciale. Per la vetrina è sufficiente un Codice CIN valido (obbligatorio per legge dal DL 145/2023 per tutte le strutture ricettive italiane)."
                : "Only for commercial operation. For the public showcase you only need a valid CIN (mandatory by Italian law DL 145/2023).",
            },
            {
              q: IT ? "Come vengono pagati i miei incassi?" : "How am I paid out?",
              a: IT
                ? "Direttamente sul tuo conto tramite Stripe Connect. Bonifico entro 2-3 giorni lavorativi. Le commissioni HospitalityHub sono trattenute automaticamente alla fonte."
                : "Directly to your bank via Stripe Connect. Payout in 2-3 business days. HospitalityHub commissions are automatically withheld at source.",
            },
          ].map((item, i) => (
            <div key={i} className="border-b border-border pb-6 last:border-0">
              <div className="font-display text-xl mb-2">{item.q}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Boost / Premium visibility */}
      <section id="boost" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2 flex items-center justify-center gap-1.5">
            <Zap className="w-3 h-3"/>{IT ? "Visibilità premium" : "Premium visibility"}
          </div>
          <h2 className="font-display text-4xl mb-3">
            {IT ? "Sali in cima con i boost Featured, Premium e Top" : "Rise to the top with Featured, Premium and Top boosts"}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {IT
              ? "Boost globale su tutta la directory oppure slot city-specific dedicato a una singola città. Ogni piano dura 30 giorni ed è ricaricabile in autonomia."
              : "Global boost across the whole directory or city-specific slots. Every plan lasts 30 days and can be renewed on demand."}
          </p>
        </div>
        {authed ? (
          <BoostCard/>
        ) : (
          <div className="text-center border border-dashed border-border rounded-lg p-10 bg-card">
            <p className="text-sm text-muted-foreground mb-4">
              {IT ? "Accedi per attivare uno slot boost sulla tua struttura." : "Log in to activate a boost slot on your property."}
            </p>
            <Link to="/auth?mode=login">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="boost-login-cta">
                {IT ? "Accedi e attiva" : "Log in to activate"} <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </Link>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h3 className="font-display text-4xl mb-4">
          {IT ? "Pronto a iniziare?" : "Ready to start?"}
        </h3>
        <p className="text-muted-foreground mb-6">
          {IT
            ? "Registrazione gratis, nessuna carta richiesta. Prova Pro 30 giorni senza impegno."
            : "Sign up free, no card required. Try Pro for 30 days risk-free."}
        </p>
        <Link to="/auth?mode=register">
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="pricing-cta-register">
            {IT ? "Iscrivi la tua struttura" : "Register your property"} <ArrowRight className="w-4 h-4 ml-1"/>
          </Button>
        </Link>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SpecialOffersSection — dynamic promo banners fetched from `/api/offers/active`.
// Renders nothing when there are no active offers, so the page layout collapses
// cleanly when superadmins pause all promos.
// -----------------------------------------------------------------------------
function SpecialOffersSection({ IT }) {
  const [offers, setOffers] = useState([]);
  useEffect(() => {
    api.get("/offers/active").then((r) => setOffers(r.data?.items || [])).catch(() => setOffers([]));
  }, []);
  if (!offers.length) return null;
  const kindStyles = {
    free_trial:      { icon: Timer,  ring: "#8a5a2b", bg: "#fff8e6" },
    discount_percent:{ icon: Tag,    ring: "#c55a3c", bg: "#fdecdd" },
    discount_fixed:  { icon: Tag,    ring: "#c55a3c", bg: "#fdecdd" },
    custom:          { icon: Gift,   ring: "#8AA88F", bg: "#eff5ef" },
  };
  const primary = offers[0];
  const secondary = offers.slice(1);
  const S = (k) => kindStyles[k] || kindStyles.custom;
  return (
    <section
      className="max-w-5xl mx-auto px-6 pt-4 pb-8"
      data-testid="pricing-special-offers"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
        <span className="uppercase tracking-[0.3em] text-[10px] font-semibold text-primary">
          {IT ? "Offerte Speciali · Solo per un tempo limitato" : "Special Offers · Limited time"}
        </span>
      </div>

      {/* Primary offer — full-width parchment banner */}
      <PrimaryOfferBanner offer={primary} IT={IT} style={S(primary.kind)}/>

      {/* Secondary offers stacked below */}
      {secondary.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="secondary-offers">
          {secondary.map((o) => (
            <SecondaryOfferCard key={o.id} offer={o} IT={IT} style={S(o.kind)}/>
          ))}
        </div>
      )}
    </section>
  );
}

function PrimaryOfferBanner({ offer, IT, style }) {
  const Icon = style.icon;
  const title = IT ? offer.title_it : (offer.title_en || offer.title_it);
  const description = IT ? offer.description_it : (offer.description_en || offer.description_it);
  const badge = IT ? offer.badge_it : (offer.badge_en || offer.badge_it);
  const ctaLabel = IT ? offer.cta_label_it : (offer.cta_label_en || offer.cta_label_it);
  return (
    <div
      className="relative rounded-lg overflow-hidden border-2 shadow-md"
      style={{ borderColor: style.ring, background: style.bg }}
      data-testid={`offer-primary-${offer.id}`}
    >
      <div className="absolute top-0 right-0 w-40 h-40 -mr-16 -mt-16 rounded-full opacity-20" style={{ background: style.ring }}/>
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 sm:p-6">
        <div className="shrink-0 w-14 h-14 rounded-full grid place-items-center" style={{ background: style.ring, color: "white" }}>
          <Icon className="w-7 h-7"/>
        </div>
        <div className="flex-1 min-w-0">
          {badge && (
            <div
              className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full mb-1.5"
              style={{ background: style.ring, color: "white" }}
              data-testid={`offer-badge-${offer.id}`}
            >
              {badge}
            </div>
          )}
          <div className="font-display text-2xl sm:text-3xl leading-tight text-ink">
            {title}
          </div>
          {description && (
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-2xl">
              {description}
            </p>
          )}
          {offer.ends_at && <OfferCountdown endsAt={offer.ends_at} IT={IT} color={style.ring} testid={`offer-countdown-${offer.id}`}/>}
        </div>
        {offer.value && (
          <div
            className="shrink-0 font-display text-4xl sm:text-5xl leading-none px-3"
            style={{ color: style.ring }}
            data-testid={`offer-value-${offer.id}`}
          >
            {offer.value}
          </div>
        )}
        {ctaLabel && offer.cta_url && (
          <a
            href={offer.cta_url}
            className="shrink-0 inline-flex items-center gap-1.5 font-semibold px-5 py-2.5 rounded-full text-white lift"
            style={{ background: style.ring }}
            data-testid={`offer-cta-${offer.id}`}
          >
            {ctaLabel} <ArrowRight className="w-4 h-4"/>
          </a>
        )}
      </div>
    </div>
  );
}

// Live countdown to `endsAt` (ISO string). Ticks once per second and hides
// once expired. Digits animate subtly on change via `key` swap.
function OfferCountdown({ endsAt, IT, color, testid }) {
  const target = new Date(endsAt).getTime();
  const compute = () => {
    const diff = Math.max(0, target - Date.now());
    const s = Math.floor(diff / 1000);
    return {
      total: diff,
      days: Math.floor(s / 86400),
      hours: Math.floor((s % 86400) / 3600),
      minutes: Math.floor((s % 3600) / 60),
      seconds: s % 60,
    };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt]);
  if (t.total <= 0) {
    return (
      <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground" data-testid={testid}>
        <Timer className="w-3.5 h-3.5"/> {IT ? "Offerta scaduta" : "Offer expired"}
      </div>
    );
  }
  const labels = IT
    ? { d: "giorni", h: "ore", m: "min", s: "sec", finish: "Termina tra" }
    : { d: "days", h: "hrs", m: "min", s: "sec", finish: "Ends in" };
  const cell = (val, label, key) => (
    <div className="flex flex-col items-center px-2 py-1 rounded min-w-[46px]"
         style={{ background: "rgba(255,255,255,0.55)", border: `1px solid ${color}33` }}>
      <span key={val} className="font-display text-xl leading-none tabular-nums" style={{ color }}>
        {String(val).padStart(2, "0")}
      </span>
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</span>
    </div>
  );
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={testid}>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
        <Timer className="w-3 h-3 animate-pulse"/> {labels.finish}
      </span>
      <div className="flex items-center gap-1.5">
        {t.days > 0 && cell(t.days, labels.d, "d")}
        {cell(t.hours, labels.h, "h")}
        {cell(t.minutes, labels.m, "m")}
        {cell(t.seconds, labels.s, "s")}
      </div>
    </div>
  );
}

function SecondaryOfferCard({ offer, IT, style }) {
  const Icon = style.icon;
  const title = IT ? offer.title_it : (offer.title_en || offer.title_it);
  const description = IT ? offer.description_it : (offer.description_en || offer.description_it);
  const ctaLabel = IT ? offer.cta_label_it : (offer.cta_label_en || offer.cta_label_it);
  return (
    <div
      className="rounded-lg border p-4 flex gap-3 items-start bg-card"
      style={{ borderColor: style.ring }}
      data-testid={`offer-secondary-${offer.id}`}
    >
      <div className="shrink-0 w-9 h-9 rounded grid place-items-center" style={{ background: style.bg, color: style.ring }}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base leading-tight">{title}</div>
        {description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</p>}
        {ctaLabel && offer.cta_url && (
          <a href={offer.cta_url} className="text-xs font-semibold mt-1.5 inline-flex items-center gap-1 hover:underline"
             style={{ color: style.ring }} data-testid={`offer-secondary-cta-${offer.id}`}>
            {ctaLabel} <ArrowRight className="w-3 h-3"/>
          </a>
        )}
      </div>
      {offer.value && (
        <div className="shrink-0 font-display text-2xl leading-none pl-2" style={{ color: style.ring }}>
          {offer.value}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CorporateSection — bespoke tier for hotels above 60 rooms. Sits directly
// below the standard plan grid on the parchment palette. Contact form
// reveals a "Parla con la direzione generale" WhatsApp CTA (+39 334 8175 881)
// with a pre-filled Italian message once the host has submitted their intent.
// The header illustration is a 1800s ink-and-wash Grand Hotel generated with
// Nano Banana and stored under /public/corporate-grand-hotel.png.
// -----------------------------------------------------------------------------
function CorporateSection({ IT }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", hotel: "", rooms: "" });
  const [saving, setSaving] = useState(false);
  const whatsappMsg = "Buongiorno, vorrei maggiori informazioni sulla soluzione Corporate di HospitalityHub";
  const whatsappUrl = `https://wa.me/393348175881?text=${encodeURIComponent(whatsappMsg)}`;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Store lead server-side (best-effort — 404 is fine, the WhatsApp CTA
      // is the primary conversion path).
      try { await api.post("/leads/corporate", form); } catch { /* silent */ }
      setSubmitted(true);
      toast.success(IT ? "Richiesta inviata" : "Request sent");
    } finally { setSaving(false); }
  };

  return (
    <section
      className="max-w-6xl mx-auto px-6 py-16"
      data-testid="pricing-corporate"
      style={{ background: "radial-gradient(ellipse at center, #f7edd4 0%, #eddcb0 100%)", borderRadius: 12, boxShadow: "inset 0 0 60px rgba(122,79,34,0.15)", color: "#3a2a17" }}
    >
      <div className="grid md:grid-cols-2 gap-8 items-center px-6 py-6">
        <div>
          <div className="uppercase tracking-[0.35em] text-[10px] font-semibold mb-3 flex items-center gap-2" style={{ color: "#8a5a2b" }}>
            <Crown className="w-3.5 h-3.5"/> Corporate
          </div>
          <h2 className="font-display italic leading-tight mb-3" style={{ fontFamily: "'IM Fell English', Georgia, serif", fontSize: "clamp(1.85rem, 3.5vw, 2.75rem)" }}>
            {IT ? "Oltre 60 camere? Costruiamo una soluzione su misura." : "More than 60 rooms? Let's build a bespoke solution."}
          </h2>
          <p className="italic mb-5 leading-relaxed" style={{ color: "#5b3d18" }}>
            {IT
              ? "Grandi alberghi, catene e resort di prestigio meritano un piano su misura: SLA dedicata, integrazione PMS, contratti quadro e onboarding assistito."
              : "Grand hotels, chains and prestige resorts deserve a bespoke plan: dedicated SLA, PMS integration, master contracts and guided onboarding."}
          </p>
          <ul className="text-sm space-y-1.5 mb-6" style={{ color: "#3a2a17" }}>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5" style={{ color: "#8a5a2b" }}/> {IT ? "Commissioni negoziate" : "Negotiated fees"}</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5" style={{ color: "#8a5a2b" }}/> {IT ? "Integrazione PMS (Opera, Protel, Vertical)" : "PMS integration (Opera, Protel, Vertical)"}</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5" style={{ color: "#8a5a2b" }}/> {IT ? "SLA 4h + account manager senior" : "4h SLA + senior account manager"}</li>
          </ul>
          <Button
            onClick={() => setOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 lift rounded-full px-6 py-5 gap-2"
            data-testid="corporate-contact-btn"
          >
            <MessageCircle className="w-4 h-4"/> {IT ? "Contattaci" : "Contact us"}
          </Button>
        </div>

        <div className="relative rounded-md overflow-hidden shadow-lg" style={{ aspectRatio: "16 / 11", border: "2px solid rgba(122,79,34,0.35)" }}>
          <img
            src="/corporate-grand-hotel.png"
            alt="Grand Hotel — vintage ink illustration"
            className="w-full h-full object-cover"
            data-testid="corporate-illustration"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(122,79,34,0.15) 100%)" }}/>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setSubmitted(false); setForm({ name: "", hotel: "", rooms: "" }); } setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="corporate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display italic text-2xl flex items-center gap-2" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
              <Crown className="w-5 h-5 text-primary"/> {IT ? "Soluzione Corporate" : "Corporate solution"}
            </DialogTitle>
            <DialogDescription>
              {IT ? "Ci mettiamo in contatto entro 24 ore per costruire l'offerta su misura." : "We'll be in touch within 24 hours to build a bespoke offer."}
            </DialogDescription>
          </DialogHeader>

          {!submitted ? (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">{IT ? "Nome" : "Name"}*</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="corp-name"/>
              </div>
              <div>
                <Label className="text-xs">{IT ? "Nome hotel" : "Hotel name"}*</Label>
                <Input required value={form.hotel} onChange={(e) => setForm({ ...form, hotel: e.target.value })} data-testid="corp-hotel"/>
              </div>
              <div>
                <Label className="text-xs">{IT ? "Numero di camere" : "Number of rooms"}*</Label>
                <Input required type="number" min="1" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} data-testid="corp-rooms"/>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="corp-submit">
                {saving ? (IT ? "Invio…" : "Sending…") : (IT ? "Invia richiesta" : "Send request")}
              </Button>
            </form>
          ) : (
            <div className="space-y-4 py-2 text-center" data-testid="corp-thankyou">
              <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 grid place-items-center">
                <Check className="w-7 h-7 text-primary"/>
              </div>
              <div>
                <div className="font-display italic text-xl mb-1" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
                  {IT ? "Richiesta ricevuta" : "Request received"}
                </div>
                <p className="text-sm text-muted-foreground italic">
                  {IT
                    ? "Per una risposta immediata puoi scrivere direttamente alla direzione generale su WhatsApp."
                    : "For an immediate reply you can WhatsApp our general management directly."}
                </p>
              </div>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1eb85a] text-white font-semibold px-6 py-3 rounded-full transition-colors"
                data-testid="corp-whatsapp"
              >
                <MessageCircle className="w-4 h-4"/>
                {IT ? "Parla con la direzione generale" : "Talk to general management"}
              </a>
              <div className="text-[10px] text-muted-foreground">+39 334 8175 881</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
